import { CookieIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useBranding } from '@/lib/branding';
import { useCookieConsent } from '@/lib/consent';
import { cn } from '@/lib/utils';

/**
 * Cookie notice: a small card anchored bottom-right (full width on phones) that rises in once and
 * stays out of the way. It tells the truth about what the app stores: one strictly necessary
 * sign-in cookie plus device preferences, no advertising or cross-site tracking. One tap accepts;
 * "Manage" reveals the single optional switch (analytics, off by default and unused until a tool is
 * wired in). The choice is kept per browser and can be revisited from the footer. Mounted outside the
 * router (main.tsx), so its one link is a plain anchor.
 */
export function CookieConsent() {
  const { branding } = useBranding();
  const consent = useCookieConsent();
  const [manage, setManage] = useState(false);
  const [analytics, setAnalytics] = useState(consent.analytics);

  if (!consent.visible) return null;

  return (
    <aside
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-desc"
      className={cn(
        'bg-card text-card-foreground border-t-brand-accent fixed inset-x-4 bottom-4 z-50 border border-t-2 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.45)] sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[400px]',
        'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-4 fill-mode-both duration-500',
      )}
    >
      <div className="flex items-start gap-3.5 p-5">
        <span className="bg-muted text-foreground grid size-9 shrink-0 place-items-center" aria-hidden>
          <CookieIcon className="size-4.5" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 id="cookie-title" className="font-display text-xl leading-none tracking-tight">
            Cookies, kept simple
          </h2>
          <p id="cookie-desc" className="text-muted-foreground mt-2 text-sm leading-relaxed">
            {branding.appName} sets one strictly necessary cookie to keep you signed in, and remembers your theme and layout on this device. No advertising, no cross-site tracking.{' '}
            <a href={branding.legal.privacyUrl ?? '/legal/privacy'} className="text-foreground underline underline-offset-4">
              Privacy policy
            </a>
          </p>

          {manage && (
            <div className="mt-4 divide-y border">
              <Row title="Strictly necessary" hint="Sign-in session and security. Always on." control={<Switch checked disabled aria-label="Strictly necessary cookies, always on" />} />
              <Row title="Preferences" hint="Theme, sidebar width and similar, stored on this device only." control={<Switch checked disabled aria-label="Preference storage, always on" />} />
              <Row title="Product analytics" hint="Anonymous usage statistics to improve the product. Nothing is collected today." control={<Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Product analytics" />} />
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => consent.decide(manage ? analytics : true)}>
              {manage ? 'Save choices' : 'Got it'}
            </Button>
            {manage ? (
              <Button size="sm" variant="outline" onClick={() => consent.decide(false)}>
                Essential only
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setManage(true)}>
                Manage
              </Button>
            )}
          </div>
        </div>
        {consent.decidedAt && (
          <button type="button" onClick={consent.dismissSettings} className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 grid size-7 shrink-0 place-items-center transition-colors" aria-label="Close">
            <XIcon className="size-4" />
          </button>
        )}
      </div>
    </aside>
  );
}

function Row({ title, hint, control }: { title: string; hint: string; control: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-muted-foreground text-xs leading-relaxed">{hint}</div>
      </div>
      {control}
    </div>
  );
}
